import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Switch } from './ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import {
  Plus, Trash2, Save, Eye, ChevronDown, ChevronUp, Layers,
  Gift, HelpCircle, Sparkles, GripVertical, AlertCircle,
  CheckCircle2, Loader2, Palette, Coins, ArrowLeft, Lock,
} from 'lucide-react';
import { FONTS, SectionHeader, Card } from './creditsGuideShared';

const EditorView = ({
  guide, meta, dirty, saving, onBack, onSave, onPreview,
  setGuide, setDirty,
}) => {
  const [expandedCat, setExpandedCat] = useState(null);

  const setG = (updater) => {
    setGuide((g) => (typeof updater === 'function' ? updater(g) : updater));
    setDirty(true);
  };

  // Mutators
  const updateHeader = (patch) => setG((g) => ({ ...g, header: { ...g.header, ...patch } }));
  const setName = (name) => setG((g) => ({ ...g, name }));
  const setDescription = (description) => setG((g) => ({ ...g, description }));

  const addFreeAction = () => setG((g) => ({ ...g, free_actions: [...(g.free_actions || []), { label: 'Nouvelle action', description: '' }] }));
  const updateFreeAction = (i, patch) => setG((g) => ({ ...g, free_actions: g.free_actions.map((a, idx) => (idx === i ? { ...a, ...patch } : a)) }));
  const removeFreeAction = (i) => setG((g) => ({ ...g, free_actions: g.free_actions.filter((_, idx) => idx !== i) }));

  const addCategory = () => setG((g) => ({
    ...g,
    categories: [...(g.categories || []), { key: `cat_${Date.now()}`, label: 'Nouvelle catégorie', icon: 'Coins', color: 'blue', expert_only: false, visible: true, actions: [] }],
  }));
  const updateCategory = (i, patch) => setG((g) => ({ ...g, categories: g.categories.map((cat, idx) => (idx === i ? { ...cat, ...patch } : cat)) }));
  const removeCategory = (i) => {
    if (!window.confirm('Supprimer cette catégorie et toutes ses actions ?')) return;
    setG((g) => ({ ...g, categories: g.categories.filter((_, idx) => idx !== i) }));
  };
  const moveCategory = (i, dir) => setG((g) => {
    const arr = [...g.categories];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return g;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...g, categories: arr };
  });
  const addAction = (catIdx) => setG((g) => ({
    ...g,
    categories: g.categories.map((cat, idx) => idx === catIdx
      ? { ...cat, actions: [...cat.actions, { code: `ACT_${Date.now()}`, label: 'Nouvelle action', credits: 1, description: '', visible: true }] }
      : cat),
  }));
  const updateAction = (catIdx, actIdx, patch) => setG((g) => ({
    ...g,
    categories: g.categories.map((cat, idx) => idx === catIdx
      ? { ...cat, actions: cat.actions.map((a, ai) => (ai === actIdx ? { ...a, ...patch } : a)) }
      : cat),
  }));
  const removeAction = (catIdx, actIdx) => setG((g) => ({
    ...g,
    categories: g.categories.map((cat, idx) => idx === catIdx
      ? { ...cat, actions: cat.actions.filter((_, ai) => ai !== actIdx) }
      : cat),
  }));

  const addHow = () => setG((g) => ({ ...g, how_it_works: [...(g.how_it_works || []), { title: 'Étape', description: '' }] }));
  const updateHow = (i, patch) => setG((g) => ({ ...g, how_it_works: g.how_it_works.map((h, idx) => (idx === i ? { ...h, ...patch } : h)) }));
  const removeHow = (i) => setG((g) => ({ ...g, how_it_works: g.how_it_works.filter((_, idx) => idx !== i) }));

  const addType = () => setG((g) => ({ ...g, credit_types: [...(g.credit_types || []), { key: `type_${Date.now()}`, label: 'Type', description: '', color: 'blue' }] }));
  const updateType = (i, patch) => setG((g) => ({ ...g, credit_types: g.credit_types.map((t, idx) => (idx === i ? { ...t, ...patch } : t)) }));
  const removeType = (i) => setG((g) => ({ ...g, credit_types: g.credit_types.filter((_, idx) => idx !== i) }));

  return (
    <div className="space-y-4">
      {/* Control bar */}
      <Card className="p-4 md:p-5">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <Button variant="outline" size="sm" onClick={onBack} className="flex-shrink-0 border-slate-300" data-testid="back-to-library-btn">
              <ArrowLeft className="h-4 w-4 mr-1" />Bibliothèque
            </Button>
            <div className="min-w-0 flex-1">
              <Label className="text-[10px] uppercase tracking-[0.2em] font-medium text-slate-500" style={FONTS.outfit}>
                Nom du guide
              </Label>
              <Input
                value={guide.name || ''}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 font-semibold text-base bg-slate-50 border-slate-200 focus:border-[#0a1e3f] focus:ring-1 focus:ring-[#0a1e3f]"
                placeholder="Nom du guide"
                data-testid="guide-name-input"
              />
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {guide.is_factory && (
              <Badge className="bg-amber-600 text-white border border-amber-800 text-[10px] uppercase tracking-wider font-semibold">
                <Lock className="h-3 w-3 mr-1" />Factory
              </Badge>
            )}
            {dirty && (
              <div className="flex items-center gap-1.5 text-amber-700 text-xs font-medium px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertCircle className="h-3.5 w-3.5" />Modifications non enregistrées
              </div>
            )}
            <Button variant="outline" onClick={onPreview} className="border-slate-300 hover:bg-slate-50" data-testid="preview-btn">
              <Eye className="h-4 w-4 mr-2" />Aperçu
            </Button>
            <Button onClick={onSave} disabled={saving || !dirty} className="bg-[#0a1e3f] hover:bg-[#0B3C5D] text-white disabled:opacity-50" data-testid="save-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              {saving ? 'Sauvegarde…' : 'Sauvegarder'}
            </Button>
          </div>
        </div>
        <div className="mt-3">
          <Label className="text-[10px] uppercase tracking-[0.2em] font-medium text-slate-500" style={FONTS.outfit}>
            Description interne (visible uniquement par les Super Admin)
          </Label>
          <Input
            value={guide.description || ''}
            onChange={(e) => setDescription(e.target.value)}
            className="mt-1 bg-slate-50 border-slate-200 text-sm"
            placeholder="À quoi sert ce guide ? Pour quelle cible ?"
            data-testid="guide-description-input"
          />
        </div>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="header" className="w-full">
        <Card className="overflow-hidden">
          <TabsList className="grid grid-cols-2 md:grid-cols-5 w-full h-auto p-1 bg-slate-100/80 rounded-none border-b border-slate-200 gap-0" style={FONTS.outfit}>
            <TabsTrigger value="header" data-testid="tab-header" className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-[#0a1e3f] text-slate-600 rounded-md py-2.5 text-xs md:text-sm font-medium transition-all">
              <Palette className="h-4 w-4 mr-1.5 hidden md:inline-block" />En-tête
            </TabsTrigger>
            <TabsTrigger value="free" data-testid="tab-free" className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-[#0a1e3f] text-slate-600 rounded-md py-2.5 text-xs md:text-sm font-medium transition-all">
              <CheckCircle2 className="h-4 w-4 mr-1.5 hidden md:inline-block" />Gratuites
              <span className="ml-1.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-emerald-600 text-white text-[10px] font-bold">{(guide.free_actions || []).length}</span>
            </TabsTrigger>
            <TabsTrigger value="cats" data-testid="tab-cats" className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-[#0a1e3f] text-slate-600 rounded-md py-2.5 text-xs md:text-sm font-medium transition-all">
              <Layers className="h-4 w-4 mr-1.5 hidden md:inline-block" />Catégories
              <span className="ml-1.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-blue-700 text-white text-[10px] font-bold">{(guide.categories || []).length}</span>
            </TabsTrigger>
            <TabsTrigger value="how" data-testid="tab-how" className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-[#0a1e3f] text-slate-600 rounded-md py-2.5 text-xs md:text-sm font-medium transition-all">
              <HelpCircle className="h-4 w-4 mr-1.5 hidden md:inline-block" />Étapes
              <span className="ml-1.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-purple-700 text-white text-[10px] font-bold">{(guide.how_it_works || []).length}</span>
            </TabsTrigger>
            <TabsTrigger value="types" data-testid="tab-types" className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-[#0a1e3f] text-slate-600 rounded-md py-2.5 text-xs md:text-sm font-medium transition-all">
              <Gift className="h-4 w-4 mr-1.5 hidden md:inline-block" />Types
              <span className="ml-1.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-rose-600 text-white text-[10px] font-bold">{(guide.credit_types || []).length}</span>
            </TabsTrigger>
          </TabsList>

          {/* HEADER */}
          <TabsContent value="header" className="p-5 md:p-6 mt-0 animate-in fade-in duration-300">
            <SectionHeader icon={Palette} title="En-tête du guide" subtitle="Titre, sous-titre et palette du bandeau affiché en haut du modal" />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="space-y-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.2em] font-medium text-slate-500" style={FONTS.outfit}>Titre principal</Label>
                  <Input value={guide.header?.title || ''} onChange={(e) => updateHeader({ title: e.target.value })} data-testid="header-title" className="mt-1 bg-slate-50 border-slate-200 focus:border-[#0a1e3f]" placeholder="Guide des Crédits ESS" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.2em] font-medium text-slate-500" style={FONTS.outfit}>Sous-titre</Label>
                  <Input value={guide.header?.subtitle || ''} onChange={(e) => updateHeader({ subtitle: e.target.value })} data-testid="header-subtitle" className="mt-1 bg-slate-50 border-slate-200 focus:border-[#0a1e3f]" placeholder="Comprendre et utiliser vos crédits efficacement" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.2em] font-medium text-slate-500" style={FONTS.outfit}>Dégradé · Départ</Label>
                    <div className="flex gap-2 mt-1">
                      <Input type="color" value={guide.header?.color_from || '#0a1e3f'} onChange={(e) => updateHeader({ color_from: e.target.value })} className="w-14 p-1 h-10 cursor-pointer border-slate-200" />
                      <Input value={guide.header?.color_from || '#0a1e3f'} onChange={(e) => updateHeader({ color_from: e.target.value })} className="flex-1 bg-slate-50 border-slate-200 font-mono text-xs" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.2em] font-medium text-slate-500" style={FONTS.outfit}>Dégradé · Arrivée</Label>
                    <div className="flex gap-2 mt-1">
                      <Input type="color" value={guide.header?.color_to || '#0B3C5D'} onChange={(e) => updateHeader({ color_to: e.target.value })} className="w-14 p-1 h-10 cursor-pointer border-slate-200" />
                      <Input value={guide.header?.color_to || '#0B3C5D'} onChange={(e) => updateHeader({ color_to: e.target.value })} className="flex-1 bg-slate-50 border-slate-200 font-mono text-xs" />
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] font-medium text-slate-500" style={FONTS.outfit}>Aperçu live</Label>
                <div className="mt-1 relative rounded-xl overflow-hidden shadow-lg ring-1 ring-slate-200">
                  <div className="relative text-white p-5" style={{ background: `linear-gradient(135deg, ${guide.header?.color_from || '#0a1e3f'} 0%, ${guide.header?.color_to || '#0B3C5D'} 100%)` }}>
                    <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#fbbf24] to-transparent" />
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center">
                        <Coins className="h-6 w-6 text-[#fbbf24]" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-[#fbbf24]/90 mb-1" style={FONTS.outfit}>O'SCOP · Unités de Crédits de Contribution</div>
                        <h2 className="text-xl font-bold leading-tight" style={FONTS.playfair}>{guide.header?.title || 'Titre du guide'}</h2>
                        {guide.header?.subtitle && <p className="text-sm text-white/80 mt-1">{guide.header.subtitle}</p>}
                      </div>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#fbbf24]/60 to-transparent" />
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                  <Sparkles className="h-3 w-3 text-[#fbbf24]" />L&apos;aperçu reflète vos modifications en temps réel.
                </p>
              </div>
            </div>
          </TabsContent>

          {/* FREE ACTIONS */}
          <TabsContent value="free" className="p-5 md:p-6 mt-0 animate-in fade-in duration-300">
            <SectionHeader
              icon={CheckCircle2}
              title="Actions Gratuites"
              subtitle="Liste des consultations / brouillons qui ne consomment aucun crédit"
              action={<Button size="sm" onClick={addFreeAction} data-testid="add-free-btn" className="bg-emerald-700 hover:bg-emerald-800 text-white"><Plus className="h-4 w-4 mr-1" />Ajouter</Button>}
            />
            <div className="space-y-2">
              {(guide.free_actions || []).length === 0 && (
                <div className="text-center py-12 border border-dashed border-slate-200 rounded-xl">
                  <CheckCircle2 className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">Aucune action gratuite définie.</p>
                  <Button size="sm" variant="outline" onClick={addFreeAction} className="mt-3"><Plus className="h-4 w-4 mr-1" />Ajouter la première</Button>
                </div>
              )}
              {(guide.free_actions || []).map((fa, i) => (
                <div key={i} className="group grid grid-cols-12 gap-2 items-center bg-gradient-to-r from-emerald-50/40 to-transparent border border-emerald-100/80 rounded-lg px-3 py-2 hover:shadow-sm transition-all">
                  <div className="col-span-1 flex justify-center">
                    <span className="w-5 h-5 rounded-full bg-emerald-700 text-white flex items-center justify-center text-[10px] font-bold">{i + 1}</span>
                  </div>
                  <Input className="col-span-4 bg-white border-slate-200" value={fa.label} placeholder="Libellé" onChange={(e) => updateFreeAction(i, { label: e.target.value })} />
                  <Input className="col-span-6 bg-white border-slate-200" value={fa.description} placeholder="Description courte" onChange={(e) => updateFreeAction(i, { description: e.target.value })} />
                  <Button variant="ghost" size="icon" onClick={() => removeFreeAction(i)} className="col-span-1 text-slate-400 hover:text-rose-600 hover:bg-rose-50 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* CATEGORIES */}
          <TabsContent value="cats" className="p-5 md:p-6 mt-0 animate-in fade-in duration-300">
            <SectionHeader
              icon={Layers}
              title="Catégories &amp; Actions"
              subtitle="Organisation des actions productives et leur coût en crédits"
              action={<Button onClick={addCategory} data-testid="add-cat-btn" className="bg-[#0a1e3f] hover:bg-[#0B3C5D] text-white"><Plus className="h-4 w-4 mr-1" />Catégorie</Button>}
            />
            <div className="space-y-3">
              {(guide.categories || []).length === 0 && (
                <div className="text-center py-12 border border-dashed border-slate-200 rounded-xl">
                  <Layers className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">Aucune catégorie créée.</p>
                  <Button size="sm" variant="outline" onClick={addCategory} className="mt-3"><Plus className="h-4 w-4 mr-1" />Créer la première catégorie</Button>
                </div>
              )}
              {(guide.categories || []).map((cat, i) => (
                <div key={i} className={`rounded-xl border ${expandedCat === i ? 'border-[#0a1e3f] shadow-md' : 'border-slate-200 hover:shadow-sm'} bg-white transition-all overflow-hidden ${!cat.visible ? 'opacity-60' : ''}`}>
                  <div className="flex items-center justify-between gap-2 p-3 md:p-4 bg-gradient-to-r from-slate-50/50 to-transparent">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <div className="flex flex-col gap-0.5">
                        <button onClick={() => moveCategory(i, -1)} disabled={i === 0} className="w-6 h-5 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors disabled:opacity-30 flex items-center justify-center"><ChevronUp className="h-3.5 w-3.5" /></button>
                        <button onClick={() => moveCategory(i, 1)} disabled={i === guide.categories.length - 1} className="w-6 h-5 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors disabled:opacity-30 flex items-center justify-center"><ChevronDown className="h-3.5 w-3.5" /></button>
                      </div>
                      <GripVertical className="h-4 w-4 text-slate-300 flex-shrink-0" />
                      <Input value={cat.label} onChange={(e) => updateCategory(i, { label: e.target.value })} className="flex-1 font-semibold border-transparent bg-transparent hover:bg-slate-50 focus:bg-white focus:border-slate-300 transition-all" />
                      <Badge variant="outline" className="bg-slate-800 text-white border-slate-800 text-xs flex-shrink-0 font-semibold">{cat.actions.length} action{cat.actions.length > 1 ? 's' : ''}</Badge>
                      {cat.expert_only && <Badge className="bg-rose-700 text-white border-0 text-[10px] uppercase tracking-wider flex-shrink-0 font-semibold">Expert</Badge>}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-slate-50 border border-slate-200">
                        <Switch checked={cat.visible} onCheckedChange={(v) => updateCategory(i, { visible: v })} />
                        <span className="text-[10px] uppercase tracking-wider font-medium text-slate-600">{cat.visible ? 'Visible' : 'Masquée'}</span>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => setExpandedCat(expandedCat === i ? null : i)} className="text-slate-600">{expandedCat === i ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</Button>
                      <Button variant="ghost" size="icon" onClick={() => removeCategory(i)} className="text-slate-400 hover:text-rose-600 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>

                  {expandedCat === i && (
                    <div className="px-4 pb-4 space-y-4 border-t border-slate-100">
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 pt-4">
                        <div>
                          <Label className="text-[10px] uppercase tracking-widest font-medium text-slate-500" style={FONTS.outfit}>Clé interne</Label>
                          <Input value={cat.key} onChange={(e) => updateCategory(i, { key: e.target.value.replace(/\s+/g, '_').toLowerCase() })} className="mt-1 bg-slate-50 border-slate-200 font-mono text-xs" />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-widest font-medium text-slate-500" style={FONTS.outfit}>Icône</Label>
                          <Select value={cat.icon} onValueChange={(v) => updateCategory(i, { icon: v })}>
                            <SelectTrigger className="mt-1 bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
                            <SelectContent>{(meta.icons || []).map((ic) => <SelectItem key={ic} value={ic}>{ic}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-widest font-medium text-slate-500" style={FONTS.outfit}>Couleur</Label>
                          <Select value={cat.color} onValueChange={(v) => updateCategory(i, { color: v })}>
                            <SelectTrigger className="mt-1 bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
                            <SelectContent>{(meta.colors || []).map((c) => <SelectItem key={c} value={c}><span className="capitalize">{c}</span></SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-widest font-medium text-slate-500" style={FONTS.outfit}>Réservation</Label>
                          <div className="mt-1 flex items-center gap-2 px-3 py-2 rounded-md bg-slate-50 border border-slate-200">
                            <Switch checked={cat.expert_only} onCheckedChange={(v) => updateCategory(i, { expert_only: v })} />
                            <span className="text-xs text-slate-700">Expert seulement</span>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <h4 className="text-xs uppercase tracking-[0.2em] font-medium text-slate-500" style={FONTS.outfit}>Actions ({cat.actions.length})</h4>
                          <Button size="sm" variant="outline" onClick={() => addAction(i)} className="border-slate-300"><Plus className="h-3.5 w-3.5 mr-1" />Action</Button>
                        </div>
                        {cat.actions.length === 0 && <p className="text-xs text-slate-400 italic text-center py-3">Aucune action dans cette catégorie.</p>}
                        {cat.actions.map((a, ai) => (
                          <div key={ai} className="group grid grid-cols-12 gap-2 items-center p-2 bg-slate-50 hover:bg-white border border-slate-200 rounded-lg transition-all">
                            <Input className="col-span-2 bg-white font-mono text-[11px]" placeholder="CODE" value={a.code} onChange={(e) => updateAction(i, ai, { code: e.target.value.toUpperCase() })} />
                            <Input className="col-span-3 bg-white" placeholder="Libellé" value={a.label} onChange={(e) => updateAction(i, ai, { label: e.target.value })} />
                            <div className="col-span-1 relative">
                              <Input type="number" min={0} value={a.credits} onChange={(e) => updateAction(i, ai, { credits: parseInt(e.target.value, 10) || 0 })} className="bg-white pr-7 text-center font-semibold tabular-nums" />
                              <Coins className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#fbbf24] pointer-events-none" />
                            </div>
                            <Input className="col-span-4 bg-white text-xs" placeholder="Description" value={a.description} onChange={(e) => updateAction(i, ai, { description: e.target.value })} />
                            <div className="col-span-1 flex items-center justify-center"><Switch checked={a.visible} onCheckedChange={(v) => updateAction(i, ai, { visible: v })} /></div>
                            <Button variant="ghost" size="icon" onClick={() => removeAction(i, ai)} className="col-span-1 text-slate-400 hover:text-rose-600 hover:bg-rose-50 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>

          {/* HOW IT WORKS */}
          <TabsContent value="how" className="p-5 md:p-6 mt-0 animate-in fade-in duration-300">
            <SectionHeader
              icon={HelpCircle}
              title="Onglet « Comment ça marche ? »"
              subtitle="Étapes pédagogiques expliquant le fonctionnement des crédits"
              action={<Button size="sm" onClick={addHow} className="bg-[#0a1e3f] hover:bg-[#0B3C5D] text-white"><Plus className="h-4 w-4 mr-1" />Étape</Button>}
            />
            <ol className="relative space-y-3 pl-6 border-l-2 border-dashed border-slate-200 ml-3">
              {(guide.how_it_works || []).length === 0 && (
                <div className="text-center py-8 -ml-9 border border-dashed border-slate-200 rounded-xl">
                  <HelpCircle className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">Aucune étape configurée.</p>
                </div>
              )}
              {(guide.how_it_works || []).map((h, i) => (
                <li key={i} className="group relative">
                  <div className="absolute -left-[1.95rem] top-2 w-8 h-8 rounded-full bg-white border-2 border-[#0a1e3f] flex items-center justify-center shadow-sm">
                    <span className="text-xs font-bold text-[#0a1e3f] tabular-nums" style={FONTS.outfit}>{i + 1}</span>
                  </div>
                  <div className="grid grid-cols-12 gap-2 p-3 bg-white border border-slate-200 rounded-lg hover:shadow-sm transition-all">
                    <Input className="col-span-3 bg-slate-50 border-slate-200 font-semibold" placeholder="Titre" value={h.title} onChange={(e) => updateHow(i, { title: e.target.value })} />
                    <Textarea className="col-span-8 bg-slate-50 border-slate-200 text-sm" placeholder="Description pédagogique" value={h.description} onChange={(e) => updateHow(i, { description: e.target.value })} rows={2} />
                    <Button variant="ghost" size="icon" onClick={() => removeHow(i)} className="col-span-1 text-slate-400 hover:text-rose-600 hover:bg-rose-50 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </li>
              ))}
            </ol>
          </TabsContent>

          {/* TYPES */}
          <TabsContent value="types" className="p-5 md:p-6 mt-0 animate-in fade-in duration-300">
            <SectionHeader
              icon={Gift}
              title="Types de Crédits"
              subtitle="Variantes de crédits affichées aux utilisateurs (standard, bonus, expert…)"
              action={<Button size="sm" onClick={addType} className="bg-[#0a1e3f] hover:bg-[#0B3C5D] text-white"><Plus className="h-4 w-4 mr-1" />Type</Button>}
            />
            <div className="space-y-2">
              {(guide.credit_types || []).length === 0 && (
                <div className="text-center py-8 border border-dashed border-slate-200 rounded-xl">
                  <Gift className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">Aucun type de crédit configuré.</p>
                </div>
              )}
              {(guide.credit_types || []).map((t, i) => (
                <div key={i} className="group grid grid-cols-12 gap-2 items-start p-3 bg-white border border-slate-200 hover:shadow-sm rounded-lg transition-all">
                  <Input className="col-span-2 bg-slate-50 border-slate-200 font-mono text-xs" placeholder="clé" value={t.key} onChange={(e) => updateType(i, { key: e.target.value })} />
                  <Input className="col-span-3 bg-slate-50 border-slate-200 font-semibold" placeholder="Libellé" value={t.label} onChange={(e) => updateType(i, { label: e.target.value })} />
                  <Textarea className="col-span-5 bg-slate-50 border-slate-200 text-sm" placeholder="Description" value={t.description} onChange={(e) => updateType(i, { description: e.target.value })} rows={2} />
                  <Select value={t.color} onValueChange={(v) => updateType(i, { color: v })}>
                    <SelectTrigger className="col-span-1 bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
                    <SelectContent>{(meta.colors || []).map((c) => <SelectItem key={c} value={c}><span className="capitalize">{c}</span></SelectItem>)}</SelectContent>
                  </Select>
                  <Button variant="ghost" size="icon" onClick={() => removeType(i)} className="col-span-1 text-slate-400 hover:text-rose-600 hover:bg-rose-50 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          </TabsContent>
        </Card>
      </Tabs>
    </div>
  );
};

export default EditorView;
